import { getPathname } from '@/i18n/navigation'
import type { queryHelpTooltips } from '@/shared/graphql/queries/sharedContentful/helpTooltipCollection'
import { rejectNil } from '@/shared/utils/array'

import { countryConfig } from '../countryConfig'
import { browserEnvs, serverEnvs } from '../envs'
import type { Locale } from '../locale'
import { getDefaultLocale, getPaginatedPath, getPathPrefix } from '../locale'
import type { AppRoute, StaticRoute } from '../routes'
import { routes } from '../routes'
import type {
  AnyEntry,
  AnyPageEntry,
  ArticleEntry,
  EnterprisePageEntry,
  LinkEntry,
  PageEntry,
  PaginatedParams,
} from './types'
import { isNotNullOrUndefined } from '../utils/isNotNullOrUndefined'
import { addHttpsProtocol } from '../utils/url'
import {
  isArticleEntry,
  isTypeEnterprisePageEntry,
  isTypePageEntry,
} from './types/contentTypeGuards'

/**
 * Slug fields list in all supported locales, extend this list if you add new locale
 */
const SLUG_FIELDS = [
  'slug',
  'slug_sv_SE',
  'slug_fi_FI',
  'slug_sv_FI',
  'slug_en_FI',
  'slug_nb_NO',
] as const

export type SlugField = (typeof SLUG_FIELDS)[number]
export type Page = Pick<
  PageEntry | EnterprisePageEntry,
  SlugField | 'title' | 'breadcrumb' | '__typename' | 'parentPage'
>

export type PathParams =
  | Page
  | Pick<ArticleEntry, SlugField | 'title' | 'breadcrumb' | '__typename' | 'customerType'>

export const isValidSlugFieldName = (fieldName: string): fieldName is SlugField =>
  SLUG_FIELDS.includes(fieldName)

export const getPageSlugParams = (page: PathParams, slugField: SlugField = 'slug'): string[] => {
  // Articles do not have parent page
  if (page.__typename === 'Article') {
    return [page[slugField]].filter(Boolean)
  }

  const parents = getParentPages(page)

  const stripLeadingSlash = (slug: string) => slug.replace(/^\/+/, '')

  return parents
    .map((parent) => parent[slugField])
    .concat(page[slugField])
    .filter(Boolean)
    .map(stripLeadingSlash)
    .filter(Boolean)
}

export type DynamicIntlHref =
  | {
      pathname: Extract<AppRoute, '/blog/[slug]' | '/enterprise/blog/[slug]'>
      params: {
        slug: string
      }
    }
  | {
      pathname: Extract<AppRoute, '/enterprise/[...slug]' | '/[...slug]'>
      params: {
        slug: string[]
      }
    }

export type StaticIntlHref = {
  pathname: StaticRoute
}

/**
 * Gets the next-intl navigation object for generic routs
 * On demand set asString to true to get the full path of a page or article, including its parent pages. Including segment structure.
 *
 * Should be used for any navigation purposes.
 *
 * For example for enterprisePage will add prefix /enterprise.
 * For article will append custom localized prefix.
 *
 */
export const getPageRoutePath = (
  page: PathParams,
  localeParam?: Locale,
  asString?: boolean,
): LinkHref => {
  const isValidSlug = (slugs: string[]) => slugs.length > 0 && slugs.join('/') !== '/'
  const getRoute = (): Extract<
    AppRoute,
    '/blog/[slug]' | '/enterprise/[...slug]' | '/enterprise/blog/[slug]' | '/[...slug]'
  > => {
    if (isArticleEntry(page)) {
      return page.customerType?.includes('Enterprise')
        ? routes.ENTERPRISE_BLOG_ARTICLE
        : routes.BLOG_ARTICLE
    }

    return isTypeEnterprisePageEntry(page) ? routes.ENTERPRISE_GENERIC : routes.GENERIC
  }
  const getFallbackRoute = () => {
    if (isArticleEntry(page)) {
      return page.customerType?.includes('Enterprise') ? routes.ENTERPRISE_BLOG : routes.BLOG
    }
    return isTypeEnterprisePageEntry(page) ? routes.ENTERPRISE_HOME : routes.HOME
  }

  const locale = localeParam ?? getDefaultLocale(browserEnvs.NEXT_PUBLIC_COUNTRY)

  // Define potential slug parameter to pick
  const slugCandidate = `slug_${locale}_${browserEnvs.NEXT_PUBLIC_COUNTRY}` as const
  const slugFieldName = isValidSlugFieldName(slugCandidate) ? slugCandidate : undefined

  const slugs = getPageSlugParams(page, slugFieldName)
  const pathname = getRoute()
  let intlHref: DynamicIntlHref | StaticIntlHref = {
    pathname: getFallbackRoute(),
  }

  if (isValidSlug(slugs)) {
    intlHref =
      pathname === routes.GENERIC || pathname === routes.ENTERPRISE_GENERIC
        ? {
            pathname,
            params: {
              slug: slugs,
            },
          }
        : {
            pathname,
            params: {
              slug: slugs.join('/'),
            },
          }
  }

  if (asString && locale) {
    return getPathname({ href: intlHref, locale })
  }

  return intlHref
}

/**
 * Gets the full path of a page or article, including its parent pages.
 *
 * Should be used to identify pathname with the Contentful data
 *
 * For example: `/products-and-services/addons/ev-charging`
 */
export const getPagePath = (page: PathParams): string => {
  const slugs = getPageSlugParams(page)

  return `/` + slugs.join('/')
}

/**
 * Gets the full URL of a page or article, including the base URL, locale prefix and path.
 * Should be used in scope of next-intl
 */
export const getPageUrl = (
  baseUrl: string,
  page: PathParams,
  locale: Locale | undefined,
  paginatedParams?: PaginatedParams,
) => {
  const url = `${baseUrl}${getPageRoutePath(page, locale as Locale, true)}${getPaginatedPath(paginatedParams)}`

  return url.replace(/\/$/, '') // Remove trailing slash
}

/**
 * Recursively gets all parent pages of a page or article as an array.
 * The first item is the root parent, the last item is the direct parent.
 */
export const getParentPages = (page: Page, parents: PathParams[] = []): PathParams[] => {
  const parentPage = page?.parentPage

  if (!parentPage) {
    return parents.toReversed()
  }

  parents.push(parentPage)
  return getParentPages(parentPage, parents)
}

export type LinkHref = string | DynamicIntlHref | StaticIntlHref

export const getLinkEntryUrl = (
  linkParams: Pick<LinkEntry, 'page' | 'url'>,
  locale?: Locale,
  options?: { withLocalePrefixIfNeeded?: boolean; asString?: boolean },
): LinkHref => {
  const { page, url } = linkParams
  if (page) {
    const path = getPageRoutePath(page, locale)

    if (options?.asString) {
      const pathWithLocale = getPageRoutePath(page, locale, options?.asString) as string
      const pathPrefix = getPathPrefix(browserEnvs.NEXT_PUBLIC_COUNTRY, locale)
      return options?.withLocalePrefixIfNeeded
        ? pathWithLocale
        : pathWithLocale.replace(pathPrefix, '')
    }

    return path
  }

  if (url) {
    return addHttpsProtocol(url)
  }

  // Return # to avoid empty passing empty string as href, empty href is treated as same page url and click trigger page reload
  return '#'
}

export const getAssetUrl = (
  asset: { url?: string | null } | undefined | null,
): string | undefined => {
  const url = asset?.url
  return url ? resolveAssetUrl(url) : undefined
}

export const resolveAssetUrl = (url: string): string =>
  url.startsWith('//') ? `https:${url}` : url

export const orderById = <T extends AnyEntry>(
  ids: string[],
  data?: (T | null | undefined)[],
): T[] => {
  return ids.map((id) => data?.find((d) => d?.sys?.id === id))?.filter(isNotNullOrUndefined)
}

export const parseContentfulVariables = (
  str: string,
  variables: { [key: string]: string },
): string => {
  let result = str

  for (const key in variables) {
    const regex = new RegExp(`{{${key}}}`, 'g')
    result = result.replace(regex, variables[key])
  }

  return result
}

export const resolveDrupalLink = (url: string): string => {
  const { APP_ENV, BASE_URL } = serverEnvs
  if (!url) {
    return url
  }

  try {
    const parsedUrl = new URL(url)
    const allowedHosts = ['fortum.com', 'www.fortum.com']
    // Only process URLs if the host is allowed and they don't already contain basePath
    if (allowedHosts.includes(parsedUrl.host) && !url.includes(countryConfig.basePath)) {
      const envPrefix = APP_ENV === 'stg' ? 'staging.' : APP_ENV === 'dev' ? 'dev.' : ''
      // Use a regex to match the beginning of the URL with protocol and optional "www."
      const domainRegex = new RegExp(`^https?:\\/\\/(www\\.)?fortum.com`)
      return url.replace(domainRegex, (_match, www) => {
        const subdomain = www ? 'www.' : ''
        return `https://${subdomain}${envPrefix}fortum.com`
      })
    }
  } catch {
    // If URL parsing fails, return a fallback
    return BASE_URL
  }
  return url
}

export const getPageCustomerType = (page: AnyPageEntry) => {
  if (isTypePageEntry(page)) {
    return 'private'
  }

  if (isTypeEnterprisePageEntry(page)) {
    return 'enterprise'
  }

  if (isArticleEntry(page)) {
    if (page.customerType === 'Private') {
      return 'private'
    }
    return 'enterprise'
  }

  return
}

export const findHelpTooltip = (
  items:
    | NonNullable<
        NonNullable<Awaited<ReturnType<typeof queryHelpTooltips>>['data']>['helpTooltipCollection']
      >['items']
    | undefined,
  country: typeof browserEnvs.NEXT_PUBLIC_COUNTRY,
) => {
  return rejectNil(items ?? []).find((item) => {
    return (
      (item.client == null || item.client.includes('Web')) &&
      (item.market == null || item.market.includes(country.toLowerCase()))
    )
  })
}
